import type { SkeletonProps } from './Skeleton';
import type { SkeletonProps as DsSkeletonProps, SkeletonShape } from '@natter-co/design-system';

type DsProps = {
  shape: SkeletonShape;
  className?: string;
  style?: DsSkeletonProps['style'];
  testId?: string;
  /** Sits in the flow of the line like text, rather than being a block. */
  inline: boolean;
  /** Text reserves the inherited line or explicit height and paints 60% of the box, like MUI. */
  fitsLine: boolean;
  animated: boolean;
};

const SHAPES: Record<NonNullable<SkeletonProps['variant']>, SkeletonShape> = {
  text: 'text',
  rounded: 'block',
  rectangular: 'block',
  circular: 'circle',
};

const isInlineBlockOnly = (sx: SkeletonProps['sx']) => {
  if (typeof sx !== 'object' || sx === null || Array.isArray(sx)) return false;
  const entries = Object.entries(sx);
  return entries.length === 1 && entries[0][0] === 'display' && entries[0][1] === 'inline-block';
};

/**
 * Maps the legacy MUI `Skeleton` props onto the Design System 2.0 `Skeleton`.
 *
 * TODO(HNS-264): temporary, like the other `toDsProps` mappers; retiring the flag deletes it.
 *
 * `null` sends the caller back to MUI. Cases that stay on MUI, because the DS skeleton would accept
 * the call and silently drop them:
 *   - `sx`, except the one exact value `{ display: 'inline-block' }`, which becomes `inline`;
 *   - `animation="wave"` - DS only supports a pulse or stillness;
 *   - `component` and `children` - a MUI skeleton with children takes the children's size, the DS one
 *     is sized by the caller;
 *   - any other prop with no DS counterpart.
 *
 * MUI `rounded` and `rectangular` both become the DS `block`; `rectangular` had square corners and
 * now has the block radius, which is the DS shape.
 *
 * A MUI text skeleton paints its bar at 60% of its box, with or without a `height`. The
 * DS `fit="line"` reserves the line-high or explicitly sized box and paints 60% of it.
 *
 * `width` and `height` are the caller's measurements, so they go on as a style rather than a class: a
 * number is pixels, exactly as in MUI.
 */
export const toDsProps = ({
  variant = 'text',
  width,
  height,
  className,
  sx,
  animation = 'pulse',
  'data-testid': testId,
  // The DS skeleton is always hidden from assistive technology, so this is already true.
  'aria-hidden': _ariaHidden,
  ...unsupported
}: SkeletonProps): DsProps | null => {
  if (Object.keys(unsupported).length > 0 || animation === 'wave') return null;
  if (sx !== undefined && !isInlineBlockOnly(sx)) return null;

  const hasSize = width !== undefined || height !== undefined;

  return {
    shape: SHAPES[variant],
    className,
    style: hasSize ? { width, height } : undefined,
    testId,
    inline: sx !== undefined,
    fitsLine: variant === 'text',
    animated: animation !== false,
  };
};
